import React from "react";
import { Table } from "react-bootstrap";
import Close from "../../../../../images/products/close.svg";
import Plus from "../../../../../images/products/plus.svg";
import {
  PackagePricingInputDataModel,
  PackagePricingInputModel,
} from "../models";
import {
  CustomLabel,
  FlexContainer,
  IconButton,
  InfoIcon,
  Input,
  nameof,
  StyledTable,
  theme,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { DescriptionCell, LabelCell, PricingTableWrapper } from "../styles";
import { AccountSettingsStore } from "../store";

type IProps = {
  package: PackagePricingInputModel;
  onUpdate: (data: PackagePricingInputModel) => void;
  renderAsLabels?: boolean;
};

export default class PackagePricing extends React.Component<IProps> {
  onChange(
    value: string,
    idx: number,
    units: boolean,
    price: boolean,
    originalPrice: boolean,
    cost: boolean,
    isBlur?: boolean,
  ) {
    const clonedObject = { ...this.props.package };
    const option = clonedObject.options[idx];
    if (price) {
      if (isBlur) {
        value = this.formatAmount(value);
      }
      option.price = value;
    }

    if (originalPrice) {
      if (isBlur) {
        value = this.formatAmount(value);
      }
      option.originalPrice = value;
    }
    if (units) {
      option.units = value;
    }

    if (cost) {
      if (isBlur) {
        value = this.formatAmount(value);
      }
      option.cost = value;
    }

    this.props.onUpdate(new PackagePricingInputModel(clonedObject));
  }

  onSetUpFeeChange(value: string, isBlur?: boolean) {
    if (isBlur) {
      value = this.formatAmount(value);
    }
    this.props.onUpdate(
      new PackagePricingInputModel({ ...this.props.package, setUpFee: value }),
    );
  }

  formatAmount = (value: string): string => {
    const sanitized = AccountSettingsStore.sanitizeAmount(value.toString());
    if (parseFloat(sanitized) < 0 || isNaN(parseFloat(sanitized))) {
      return "0.00";
    }
    return AccountSettingsStore.formatAmountWithoutCommas(
      parseFloat(sanitized || "0.00"),
      true,
    );
  };

  hasError(fieldName: string, index: number): boolean {
    const error = this.props.package.errors.find(
      (f) => f.index == index && f.field === fieldName,
    );
    return error !== undefined;
  }

  checkBox(
    value: boolean,
    fieldName: keyof PackagePricingInputModel,
    label: string,
  ) {
    return (
      <Input
        value={value}
        id={`chk${fieldName}`}
        type="checkbox"
        label={label}
        onChange={(e) => this.onCheckBoxChange(e.target.checked, fieldName)}
        className="show-column-chkbox"
      />
    );
  }

  onCheckBoxChange(value: boolean, fieldName: keyof PackagePricingInputModel) {
    const clonedObject = { ...this.props.package };
    switch (fieldName) {
      case nameof<PackagePricingInputModel>("showOriginalPrice"):
        this.props.onUpdate(
          new PackagePricingInputModel({
            ...clonedObject,
            showOriginalPrice: value,
          }),
        );
        break;
      case nameof<PackagePricingInputModel>("showCost"):
        this.props.onUpdate(
          new PackagePricingInputModel({ ...clonedObject, showCost: value }),
        );
        break;
    }
  }

  onAddRow() {
    const fields = this.props.package.options.map((a) => Object.assign({}, a));

    fields.push(new PackagePricingInputDataModel());

    this.props.onUpdate(
      new PackagePricingInputModel({ ...this.props.package, options: fields }),
    );
  }

  deletePackagePricing(idx: number) {
    const fields = this.props.package.options.map((a) => Object.assign({}, a));
    fields.splice(idx, 1);
    this.props.onUpdate(
      new PackagePricingInputModel({ ...this.props.package, options: fields }),
    );
  }

  get topRow() {
    const { renderAsLabels } = this.props;
    if (renderAsLabels) return null;
    return (
      <tr>
        <th colSpan={6}>
          <FlexContainer spacing={35} justifyContent="end" alignItems="center">
            {this.checkBox(
              this.props.package.showOriginalPrice,
              nameof<PackagePricingInputModel>("showOriginalPrice"),
              "Show Original Price",
            )}
            {this.checkBox(
              this.props.package.showCost,
              nameof<PackagePricingInputModel>("showCost"),
              "Show Cost",
            )}
            <IconButton
              className="add-package-btn"
              color={theme.palette.info.dark}
              type="button"
              onClick={() => this.onAddRow()}
              gap="4px"
              height="23px"
              justifyContent="flex-end"
            >
              <img src={Plus} alt="Add Row" />
              <span> Add Row</span>
            </IconButton>
          </FlexContainer>
        </th>
      </tr>
    );
  }

  getCostRow(
    index: number,
    t: PackagePricingInputDataModel,
    renderAsLabels?: boolean,
  ) {
    return (
      <>
        {this.props.package.showCost && (
          <td>
            {renderAsLabels && (
              <>
                $
                {AccountSettingsStore.formatAmount(
                  parseFloat(t.cost),
                  true,
                )}{" "}
              </>
            )}
            {!renderAsLabels && (
              <div>
                <FlexContainer
                  fxLayout="row"
                  className="dollar-input"
                  justifyContent="center"
                >
                  <div>
                    <Input
                      value={t.cost}
                      error={this.hasError(
                        nameof<PackagePricingInputDataModel>("cost"),
                        index,
                      )}
                      id={`txt_cost_${index}`}
                      type="text"
                      onChange={(e, isBlur) =>
                        this.onChange(
                          e.target.value,
                          index,
                          false,
                          false,
                          false,
                          true,
                          isBlur,
                        )
                      }
                      placeholder="0.00"
                    />
                  </div>
                </FlexContainer>
              </div>
            )}
          </td>
        )}
      </>
    );
  }

  getPriceRow(
    index: number,
    t: PackagePricingInputDataModel,
    renderAsLabels?: boolean,
  ) {
    return (
      <td>
        {renderAsLabels && (
          <>${AccountSettingsStore.formatAmount(parseFloat(t.price), true)}</>
        )}
        {!renderAsLabels && (
          <div>
            <FlexContainer
              fxLayout="row"
              className="dollar-input"
              justifyContent="center"
            >
              <div>
                <Input
                  value={t.price}
                  error={this.hasError(
                    nameof<PackagePricingInputDataModel>("price"),
                    index,
                  )}
                  id={`txt_price_${index}`}
                  type="text"
                  onChange={(e, isBlur) =>
                    this.onChange(
                      e.target.value,
                      index,
                      false,
                      true,
                      false,
                      false,
                      isBlur,
                    )
                  }
                  placeholder="0.00"
                />
              </div>
            </FlexContainer>
          </div>
        )}
      </td>
    );
  }

  getUnitstRow(
    index: number,
    t: PackagePricingInputDataModel,
    renderAsLabels?: boolean,
  ) {
    return (
      <td>
        {renderAsLabels && <>{t.units}</>}
        {!renderAsLabels && (
          <Input
            value={t.units}
            error={this.hasError(
              nameof<PackagePricingInputDataModel>("units"),
              index,
            )}
            id={`txt_units_${index}`}
            type="text"
            onChange={(e) =>
              this.onChange(e.target.value, index, true, false, false, false)
            }
            placeholder=""
          />
        )}
      </td>
    );
  }

  getOriginalPriceRow(
    index: number,
    t: PackagePricingInputDataModel,
    renderAsLabels?: boolean,
  ) {
    return (
      <>
        {" "}
        {this.props.package.showOriginalPrice && (
          <td>
            {renderAsLabels && (
              <>
                $
                {AccountSettingsStore.formatAmount(
                  parseFloat(t.originalPrice),
                  true,
                )}
              </>
            )}
            {!renderAsLabels && (
              <div>
                <FlexContainer
                  fxLayout="row"
                  className="dollar-input"
                  justifyContent="center"
                >
                  <div>
                    <Input
                      value={t.originalPrice}
                      error={this.hasError(
                        nameof<PackagePricingInputDataModel>("originalPrice"),
                        index,
                      )}
                      id={`txt_original_price_${index}`}
                      type="text"
                      onChange={(e, isBlur) =>
                        this.onChange(
                          e.target.value,
                          index,
                          false,
                          false,
                          true,
                          false,
                          isBlur,
                        )
                      }
                      placeholder="0.00"
                    />
                  </div>
                </FlexContainer>
              </div>
            )}
          </td>
        )}
      </>
    );
  }
  render(): React.ReactNode {
    const { renderAsLabels } = this.props;
    return (
      <PricingTableWrapper>
        <FlexContainer>
          <LabelCell></LabelCell>
          <DescriptionCell>
            <div className="styled-table-wrapper">
              <StyledTable>
                <Table bsPrefix="styled-table" responsive>
                  <thead>
                    {this.topRow}
                    <tr>
                      <th></th>
                      <th>UNITS</th>
                      <th>PACKAGE PRICE</th>
                      {this.props.package.showOriginalPrice && (
                        <th>ORIGINAL PRICE</th>
                      )}
                      {this.props.package.showCost && <th>PACKAGE COST</th>}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.props.package.options.map((t, index) => {
                      return (
                        <tr key={index}>
                          <td>PACKAGE {index + 1}</td>
                          {this.getUnitstRow(index, t, renderAsLabels)}
                          {this.getPriceRow(index, t, renderAsLabels)}
                          {this.getOriginalPriceRow(index, t, renderAsLabels)}
                          {this.getCostRow(index, t, renderAsLabels)}

                          <td>
                            {index > 0 && !renderAsLabels && (
                              <button
                                className="delete-row-btn"
                                onClick={() => this.deletePackagePricing(index)}
                              >
                                <img src={Close} alt="Delete Row" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </StyledTable>
            </div>
          </DescriptionCell>
        </FlexContainer>
        {this.props.package.showSetUpFee && (
          <FlexContainer
            className="label-description-row"
            padding="25px 0px 0px"
          >
            <LabelCell>
              <CustomLabel lineHeight="40px" fontSize="16px">
                {" "}
                Setup Fee
              </CustomLabel>
              <InfoIcon />
            </LabelCell>
            <DescriptionCell>
              <FlexContainer
                spacing={15}
                fxLayout="row"
                className="dollar-usd-input"
              >
                {" "}
                <div>
                  {renderAsLabels && this.props.package.setUpFee ? (
                    <>
                      $
                      {AccountSettingsStore.formatAmount(
                        parseFloat(this.props.package.setUpFee),
                      )}{" "}
                    </>
                  ) : renderAsLabels ? (
                    "-"
                  ) : (
                    ""
                  )}
                  {!renderAsLabels && (
                    <Input
                      value={this.props.package.setUpFee}
                      error={this.hasError(
                        nameof<PackagePricingInputModel>("setUpFee"),
                        -1,
                      )}
                      id={`txt_tier_price`}
                      type="text"
                      onChange={(e, isBlur) =>
                        this.onSetUpFeeChange(e.target.value, isBlur)
                      }
                    />
                  )}
                </div>
              </FlexContainer>
            </DescriptionCell>
          </FlexContainer>
        )}

        {/* <FlexContainer className="label-description-row">
                    <LabelCell></LabelCell>
                    <DescriptionCell>
                      
                    </DescriptionCell>
                </FlexContainer> */}
      </PricingTableWrapper>
    );
  }
}
